import { StatusIndicatorProps } from "@amzn/awsui-components-react-v3/polaris/status-indicator/interfaces";
import {
  KeyValuePairs,
  Popover,
  StatusIndicator,
} from "@amzn/awsui-components-react-v3";
import * as React from "react";
import { User, UserStatus } from "../services/idp/idp.types";

interface UserStatusIndicatorProps {
  user: User;
  prependString?: string;
}

const formatStatus = (args: { status: string; prependString?: string }) => {
  // if args.status is an empty string, "unknown" should be displayed, hence the use of || instead of ??
  const statusString = args.status || "unknown";
  if (args.prependString) {
    return `${args.prependString} ${args.status}`;
  }
  return statusString.charAt(0).toUpperCase() + statusString.slice(1);
};

const statusDisplay = (
  user: User,
): {
  status: string;
  statusType: StatusIndicatorProps.Type;
  additionalStatusInfo?: string;
} => {
  switch (user.status) {
    case UserStatus.Enabled:
      return {
        status: UserStatus.Enabled,
        statusType: "success",
      };
    case UserStatus.New:
      return {
        status: UserStatus.New,
        statusType: "pending",
      };
    case UserStatus.Locked:
      // If a user is locked but the lock expiration time has passed, we should display the user as active,
      // otherwise there may be confusion when it comes to performing actions on a user such as sending a
      // registration email, leading to potential tickets and other customer contacts.
      if (user.lockExpiresAt) {
        const lockExpiryTime = new Date(user.lockExpiresAt);
        if (lockExpiryTime.getTime() < new Date().getTime()) {
          return {
            status: UserStatus.Enabled,
            statusType: "success",
          };
        }
      }
      const lockedUntilDateString = user.lockExpiresAt
        ? new Date(user.lockExpiresAt).toLocaleString(undefined, {
            timeZoneName: "short",
          })
        : "never";
      return {
        status: UserStatus.Locked,
        statusType: "warning",
        additionalStatusInfo: ` (expires: ${lockedUntilDateString})`,
      };
    case UserStatus.Disabled:
      return {
        status: UserStatus.Disabled,
        statusType: "error",
      };
  }
  // defaults
  return {
    status: user.status,
    statusType: "not-started",
  };
};

export const UserStatusIndicator = ({
  user,
  prependString,
}: UserStatusIndicatorProps) => {
  const { status, statusType, additionalStatusInfo } = statusDisplay(user);
  const statusIndicatorComponent = (
    <StatusIndicator
      type={statusType}
      data-testid={`user-status-${user.alias}`}
    >
      {formatStatus({ status, prependString })}
    </StatusIndicator>
  );

  // if status is something other than success, wrap the StatusIndicator in a Popover with additional information about the status
  if (statusType !== "success") {
    const statusDateString = user.statusUpdateTimestamp
      ? new Date(user.statusUpdateTimestamp).toLocaleString(undefined, {
          timeZoneName: "short",
        })
      : "Unknown time";
    return (
      <Popover
        data-testid={`user-status-popover-${user.alias}`}
        dismissButton={false}
        position="top"
        content={
          <KeyValuePairs
            data-testid={`user-status-popover-info-${user.alias}`}
            items={[
              {
                label: `${formatStatus({ status })} since`,
                value: statusDateString,
              },
              {
                label: `${formatStatus({ status })} reason`,
                value:
                  (user.statusReason || "No reason") +
                  (additionalStatusInfo ?? ""),
              },
            ]}
          />
        }
      >
        {statusIndicatorComponent}
      </Popover>
    );
  }
  return statusIndicatorComponent;
};
